import React from "react";
import { Link } from "react-router-dom";
import SminHeader from "@/components/SminHeader";
import SminFooter from "@/components/SminFooter";
import { MARKETING_NAV as NAV } from "@/lib/marketingNav";
import {
  BookOpen, GraduationCap, Briefcase, FileText, HelpCircle, ScrollText,
  Calendar, ArrowRight, MessageCircle, Play, Bookmark, Award,
} from "lucide-react";

const CARDS = [
  { Icon: BookOpen,      title: "Documentation",   body: "Complete reference for every workflow — CSV schemas, API endpoints, LG parameters, report templates.", cta: "Read docs",       to: "/documentation" },
  { Icon: GraduationCap, title: "Tutorials",       body: "Guided walkthroughs from first drillhole import to final signed report. Video + written formats.",   cta: "Start learning",  to: "/tutorials" },
  { Icon: Briefcase,     title: "Case Studies",    body: "How consultants, junior explorers and mid-tier producers use SMIN across gold, copper, iron and lithium projects.", cta: "See case studies", to: "/case-studies" },
  { Icon: FileText,      title: "Blog",            body: "Deep dives on kriging tuning, LG constraints, dilution modelling and NI 43-101 disclosure practice.", cta: "Visit blog",      to: "/blog" },
  { Icon: HelpCircle,    title: "FAQ",             body: "Common questions on pricing, deployment, security, on-prem options and data ownership.",             cta: "Read FAQ",        to: "/faq" },
  { Icon: ScrollText,    title: "System Requirements", body: "Hardware, browser and OS matrices for a comfortable working experience — from Starter to Power User.", cta: "See requirements", to: "/system-requirements" },
];

const POPULAR = [
  { slug: "ordinary-kriging-in-plain-english",
    tag: "Estimation",    date: "12 Feb 2026", author: "Dr. Ananya Rao",
    title: "Ordinary Kriging in Plain English (Without the Sigma Notation)",
    excerpt: "A pragmatic tour of Ordinary Kriging — variograms, weights, negative kriging and why it's still the workhorse estimator in 2026." },
  { slug: "calibrating-ore-shell-iso-values",
    tag: "Modelling",     date: "04 Feb 2026", author: "Rahul Menon",
    title: "Calibrating Ore-Shell Iso Values Against Field Statistics",
    excerpt: "Picking the wrong iso-value collapses your ore shell into an implausible blob or a shattered sliver. Here's the field-statistics approach we use." },
  { slug: "lg-pit-optimisation-common-mistakes",
    tag: "Mine Planning", date: "22 Jan 2026", author: "Ivan Petrov",
    title: "The Five Most Common Lerchs–Grossmann Pit Optimisation Mistakes",
    excerpt: "From ignoring rock-domain slope zones to over-fitting revenue factors, the five sins we see most often — and the fixes that unlock a defensible optimal pit." },
];

const LEARNING = [
  { level: "Beginner",    duration: "~35 min", track: "Fundamentals",           body: "Workspace, team, first CSV import, first PDF export." },
  { level: "Intermediate", duration: "~1h 20", track: "Geology & Modelling",     body: "Compositing, domain codes, implicit surfaces, ore shells." },
  { level: "Advanced",     duration: "~1h 45", track: "Resource Estimation",     body: "Variography, kriging, classification, cross-validation." },
  { level: "Advanced",     duration: "~2h",    track: "Pit Optim & Scheduling",  body: "LG, pushbacks, NPV, sensitivity, scenarios." },
];

export default function ResourcesPage() {
  return (
    <div className="min-h-screen flex flex-col bg-white text-neutral-900" data-testid="page-resources">
      <SminHeader variant="marketing" forceGuest navLinks={NAV} />

      <main className="flex-1">
        {/* Hero */}
        <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-8 text-center">
          <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#4CAF50" }}>
            Resources
          </div>
          <h1 className="mt-3 text-3xl md:text-5xl font-bold tracking-tight max-w-3xl mx-auto leading-tight">
            Everything you need to get productive fast.
          </h1>
          <p className="mt-5 max-w-2xl mx-auto text-neutral-600 text-sm md:text-base leading-relaxed">
            Docs, tutorials, case studies, blog and a growing library of geology &amp; mining-engineering guides.
          </p>
        </section>

        {/* 6 resource cards */}
        <section className="max-w-7xl mx-auto px-6 md:px-10 pb-16 grid md:grid-cols-3 gap-5">
          {CARDS.map(({ Icon, title, body, cta, to }) => (
            <Link
              key={title} to={to}
              data-testid={`resource-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="p-6 rounded-xl border border-neutral-200 hover:border-[#4CAF50] hover:shadow-md transition block bg-white"
            >
              <span className="inline-flex items-center justify-center h-10 w-10 rounded-md" style={{ background: "#4CAF5015" }}>
                <Icon className="h-5 w-5" style={{ color: "#4CAF50" }} strokeWidth={2} />
              </span>
              <h3 className="mt-4 text-base font-bold">{title}</h3>
              <p className="mt-1.5 text-sm text-neutral-600 leading-relaxed">{body}</p>
              <div className="mt-4 text-xs font-semibold uppercase tracking-widest" style={{ color: "#4CAF50" }}>
                {cta} →
              </div>
            </Link>
          ))}
        </section>

        {/* Learning path */}
        <section className="bg-neutral-50 py-16">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 gap-3">
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#4CAF50" }}>
                  Learning path
                </div>
                <h2 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight">
                  From first login to signed report — in one weekend.
                </h2>
              </div>
              <Link to="/tutorials"
                    className="text-sm font-semibold inline-flex items-center gap-1.5"
                    style={{ color: "#4CAF50" }}>
                All tutorials <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <ol className="grid md:grid-cols-4 gap-4">
              {LEARNING.map((l, i) => (
                <li key={l.track}
                    className="bg-white p-5 rounded-xl border border-neutral-200 hover:border-[#4CAF50] transition">
                  <div className="flex items-center justify-between">
                    <span
                      className="inline-flex items-center justify-center h-8 w-8 rounded-full text-white text-xs font-bold"
                      style={{ background: "#4CAF50" }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span
                      className="text-[10px] font-mono uppercase tracking-widest text-neutral-500"
                    >
                      {l.duration}
                    </span>
                  </div>
                  <h3 className="mt-4 text-base font-bold">{l.track}</h3>
                  <div className="text-[10px] uppercase tracking-widest mt-1" style={{ color: "#4CAF50" }}>
                    {l.level}
                  </div>
                  <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{l.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Popular articles preview */}
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 gap-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#4CAF50" }}>
                Popular reads
              </div>
              <h2 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight">
                Field notes from the SMIN team.
              </h2>
            </div>
            <Link to="/blog"
                  className="text-sm font-semibold inline-flex items-center gap-1.5"
                  style={{ color: "#4CAF50" }}>
              All articles <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {POPULAR.map((p) => (
              <article
                key={p.slug}
                className="p-6 rounded-xl border border-neutral-200 hover:border-[#4CAF50] hover:shadow-md transition bg-white flex flex-col"
              >
                <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                  <span className="px-2 py-0.5 rounded" style={{ background: "#4CAF5015", color: "#4CAF50" }}>
                    {p.tag}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> {p.date}
                  </span>
                </div>
                <h3 className="mt-4 text-base font-bold tracking-tight leading-snug">{p.title}</h3>
                <p className="mt-2 text-sm text-neutral-600 leading-relaxed flex-1">{p.excerpt}</p>
                <div className="mt-4 pt-4 border-t border-neutral-100 text-xs text-neutral-500">
                  by <span className="font-semibold text-neutral-800">{p.author}</span>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Newsletter + support strip */}
        <section className="bg-neutral-50 py-14">
          <div className="max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-8">
            <div className="p-8 rounded-xl bg-white border border-neutral-200">
              <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#4CAF50" }}>
                Newsletter
              </div>
              <h3 className="mt-2 text-xl font-bold tracking-tight">Subscribe to the SMIN letter.</h3>
              <p className="mt-2 text-sm text-neutral-600 leading-relaxed">
                One email every fortnight — new posts, product releases, hand-picked links for practising geologists and mine engineers.
              </p>
              <form
                className="mt-5 flex flex-col sm:flex-row gap-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  const email = new FormData(e.currentTarget).get("email");
                  window.location.href = `mailto:letter@sresources.co?subject=${encodeURIComponent("Subscribe")}&body=${encodeURIComponent(`Please subscribe: ${email}`)}`;
                }}
              >
                <input
                  type="email" name="email" required placeholder="you@company.com"
                  data-testid="resources-newsletter-email"
                  className="flex-1 h-11 rounded-md border border-neutral-200 px-3 text-sm focus:outline-none focus:border-[#4CAF50]"
                />
                <button
                  type="submit"
                  data-testid="resources-newsletter-submit"
                  className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md text-white font-semibold text-sm"
                  style={{ background: "#4CAF50" }}
                >
                  Subscribe <ArrowRight className="h-4 w-4" />
                </button>
              </form>
            </div>

            <div className="p-8 rounded-xl bg-white border border-neutral-200">
              <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#4CAF50" }}>
                Need help now?
              </div>
              <h3 className="mt-2 text-xl font-bold tracking-tight">Talk to a human.</h3>
              <p className="mt-2 text-sm text-neutral-600 leading-relaxed">
                Real geologists and mining engineers on our team, ready to help — from stubborn variograms to JORC classification arguments.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <a
                  href="https://wa.me/919938872207"
                  target="_blank" rel="noreferrer"
                  data-testid="resources-whatsapp"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-md text-white font-semibold text-sm"
                  style={{ background: "#4CAF50" }}
                >
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>
                <Link
                  to="/support"
                  data-testid="resources-support-link"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-md border-2 font-semibold text-sm"
                  style={{ borderColor: "#4CAF50", color: "#4CAF50" }}
                >
                  Support Center
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <SminFooter />
    </div>
  );
}
